// Справка „Анулирани дейности" (slug `deleted`): хедър „Справка" с
// подзаглавие, период (7 пресета + календар, по подразбиране „Този месец") и
// Excel; KPI за периода, търсачка, филтри В ЗАГЛАВИЯТА НА КОЛОНИТЕ (Тип ·
// Анулирал · Склад) с фасетни броячи и ред „Филтри: …" над таблицата, колони,
// които сортират, „Покажи още" и ред „Общо за периода" (карти на телефон).
// Клик на ред отваря документа с „Възстанови" (`components/DeletedDetailModal`);
// менюто „⋯" на реда — Преглед · Възстанови. Периодът филтрира по ДАТАТА НА
// АНУЛИРАНЕ (не по датата на документа).
import { useCallback, useMemo, useState, type FC } from "react";
import { DeleteOutlined, EuroOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useViewport } from "../../../hooks/useViewport";
import { useDeletedMovements } from "../../../hooks/useInventory";
import type { DeletedMovement } from "../../../api/inventory";
import { exportToExcel } from "../../../utils/excelExport";
import { InventoryTheme } from "../components/InventoryTheme";
import { PageHeader } from "../components/list/PageHeader";
import { EmptyScreen, KpiGrid, SearchBox, SectionCard, type KpiCard } from "../components/list/ListBits";
import type { RowAction } from "../components/list/RowActionsMenu";
import { ReportToolbar } from "../components/list/ReportToolbar";
import { refreshOf } from "../components/list/reportRefresh";
import { formatPeriod, periodOfPreset, periodToParams, type Period } from "../components/period/periodPresets";
import { FilterSummaryLine } from "../components/filters/FilterSummaryLine";
import { allSections, useColumnFilters, type ColumnSections } from "../components/filters/useColumnFilters";
import { useShowMore } from "../components/list/useShowMore";
import { fmtDate, fmtDateTime } from "../components/dateTime";
import { formatMoney } from "../components/numbers";
import { nextSort, type LegacyReportProps } from "./components/reportListModel";
import { ReportLoading, ReportNoMatch, ResultSum, ShowMoreRow } from "./components/ReportListBits";
import {
  EMPTY_DELETED_FILTERS,
  deletedFilterSpecs,
  deletedSortFirstDir,
  deletedTotals,
  searchDeleted,
  sortDeleted,
  type DeletedFilterKey,
  type DeletedSort,
  type DeletedSortKey,
} from "./components/deletedReportModel";
import { DeletedReportTable, type DeletedDensity } from "./components/DeletedReportTable";
import { DeletedDetailModal } from "./components/DeletedDetailModal";
import { useRestoreDeleted } from "./components/useRestoreDeleted";

export const DeletedReport: FC<LegacyReportProps> = () => {
  const { t } = useTranslation();
  const { isNarrow, isWide } = useViewport();
  const { requestRestore } = useRestoreDeleted();

  const [period, setPeriod] = useState<Period>(() => periodOfPreset("thisMonth"));
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<DeletedSort | null>(null);
  const [detail, setDetail] = useState<DeletedMovement | null>(null);

  const movementsQuery = useDeletedMovements(periodToParams(period));
  const { data: movements = [], isLoading, isFetching } = movementsQuery;
  const { refresh, refreshing } = refreshOf(movementsQuery);

  // Вид на движението → етикет (ключовете са и за филтъра)
  const typeLabel = useCallback(
    (type: string) => {
      const key = `inventoryPage.deletedType_${type}`;
      const label = t(key);
      return label === key ? type : label;
    },
    [t],
  );

  // ─── Филтри в колоните (броячите — фасетни, по периода след търсенето) ──
  const specs = useMemo(
    () =>
      deletedFilterSpecs({
        titles: {
          types: t("inventoryPage.colType"),
          users: t("inventoryPage.deletedColBy"),
          warehouses: t("inventoryPage.colWarehouse"),
        },
        typeLabel,
      }),
    [t, typeLabel],
  );
  const searched = useMemo(() => searchDeleted(movements, query, typeLabel), [movements, query, typeLabel]);
  const filters = useColumnFilters({ specs, empty: EMPTY_DELETED_FILTERS, universe: movements, base: searched });
  const shown = useMemo(() => sortDeleted(filters.rows, typeLabel, sort), [filters.rows, typeLabel, sort]);
  // Низ, не масив — useShowMore нулира при СМЯНА на ключа
  const resetKey = JSON.stringify([formatPeriod(period), filters.state, query, sort]);
  const { visible, hiddenCount, showMore } = useShowMore(shown, resetKey);
  // KPI-тата са за периода; редът над таблицата — за намереното
  const kpis = deletedTotals(movements);
  const totals = deletedTotals(shown);
  const clearAll = () => {
    filters.clearAll();
    setQuery("");
  };
  const sections: ColumnSections<DeletedFilterKey> = {
    types: filters.section("types"),
    users: filters.section("users"),
    warehouses: filters.section("warehouses"),
  };

  const kpiCards: KpiCard[] = [
    { label: t("inventoryPage.deletedKpiCount"), value: String(kpis.count), Icon: DeleteOutlined },
    { label: t("inventoryPage.kpiValueGross"), value: formatMoney(kpis.gross), Icon: EuroOutlined, tone: "brand" },
  ];

  const actionsOf = (movement: DeletedMovement): RowAction[] => [
    { key: "view", label: t("inventoryPage.viewDocument"), onClick: () => setDetail(movement) },
    {
      key: "restore",
      label: t("inventoryPage.deletedRestore"),
      dividerBefore: true,
      onClick: () => requestRestore(movement),
    },
  ];

  const exportXls = () =>
    exportToExcel({
      filename: "anulirani-deinosti",
      sheetName: t("inventoryPage.deletedReport"),
      rows: shown,
      columns: [
        { header: t("inventoryPage.deletedColWhen"), value: (m) => fmtDateTime(m.deletedAt) },
        { header: t("inventoryPage.deletedColBy"), value: (m) => m.deletedByName ?? "" },
        { header: t("inventoryPage.colType"), value: (m) => typeLabel(m.type) },
        { header: t("inventoryPage.deletedColDocDate"), value: (m) => fmtDate(m.date) },
        { header: t("inventoryPage.colWarehouse"), value: (m) => m.warehouseNames.join(", ") },
        { header: t("inventoryPage.colInvoice"), value: (m) => m.invoiceNumber ?? "" },
        { header: t("inventoryPage.colReason"), value: (m) => m.reason ?? "" },
        { header: t("inventoryPage.colComment"), value: (m) => m.note ?? "" },
        { header: t("inventoryPage.colProducts"), value: (m) => m.productsCount },
        { header: t("inventoryPage.colNet"), value: (m) => m.totalValueNoVat },
        { header: t("inventoryPage.colGross"), value: (m) => m.totalValueWithVat },
      ],
    });

  const density: DeletedDensity = isNarrow ? "cards" : isWide ? "full" : "medium";
  const onSort = (key: DeletedSortKey) => setSort((current) => nextSort(current, key, deletedSortFirstDir(key)));

  return (
    <InventoryTheme>
      <div className="flex h-full flex-col bg-canvas text-ink">
        <PageHeader
          kicker={t("inventoryPage.kickerReport")}
          title={t("inventoryPage.deletedReport")}
          subtitle={isLoading ? undefined : t("inventoryPage.deletedSubtitle", { count: kpis.count })}
          right={
            <ReportToolbar
              period={period}
              onPeriodChange={setPeriod}
              filterSections={allSections(sections)}
              onExport={exportXls}
              exportDisabled={!shown.length}
              onRefresh={refresh}
              refreshing={refreshing}
            />
          }
        />
        <div
          className={`flex min-h-0 flex-1 flex-col gap-3.5 overflow-y-auto ${
            isNarrow ? "px-3 pb-10 pt-3.5" : "px-7 pb-12 pt-5"
          }`}
        >
          <KpiGrid cards={kpiCards} />
          <div className="flex flex-wrap items-center gap-2">
            <SearchBox value={query} onChange={setQuery} placeholder={t("inventoryPage.deletedSearchPh")} />
          </div>
          <SectionCard className={isFetching && !isLoading ? "opacity-60" : ""}>
            <FilterSummaryLine
              countLabel={t("inventoryPage.reportResult", { count: shown.length })}
              items={filters.summary}
              onClearAll={filters.clearAll}
            >
              <ResultSum label={t("inventoryPage.colGross")} value={formatMoney(totals.gross)} strong />
            </FilterSummaryLine>
            {isLoading ? (
              <ReportLoading />
            ) : movements.length === 0 ? (
              <EmptyScreen Icon={DeleteOutlined} tone="brand" title={t("inventoryPage.noDeleted")} />
            ) : (
              <DeletedReportTable
                rows={visible}
                density={density}
                typeLabel={typeLabel}
                sort={sort}
                onSort={onSort}
                filters={sections}
                onOpen={setDetail}
                actionsOf={actionsOf}
                totals={totals}
                footer={
                  shown.length ? (
                    <ShowMoreRow hiddenCount={hiddenCount} onShowMore={showMore} />
                  ) : (
                    <ReportNoMatch onClear={clearAll} />
                  )
                }
              />
            )}
          </SectionCard>
        </div>

        <DeletedDetailModal
          movement={detail}
          onClose={() => setDetail(null)}
          typeLabel={typeLabel}
          isNarrow={isNarrow}
        />
      </div>
    </InventoryTheme>
  );
};

export default DeletedReport;
